// MailInfoRow.tsx
import React from "react";
import {
  IconButton,
  TextField,
  Typography,
  Tooltip,
  Box,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  Grid2,
  Card,
  CardContent,
  Button,
  Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { CompanyIdIssuanceInfo, Status } from "IF/interfaceType";
import { convertNumberToFullWidthDigits } from "Utils/format";

interface CompanyInfoRowProps {
  data: CompanyIdIssuanceInfo[];
  editIndex: number | null;
  editFormData: CompanyIdIssuanceInfo;
  onEdit: (index: number) => void;
  handleChange: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
    child?: React.ReactNode
  ) => void;
  handleUpdate: () => void;
  handleCancel: () => void;
  handleDelete: (index: number) => void;
  handleConfirmModalOpen: (message: string, confirmAction: () => void) => void;
  handleTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOpen: () => void;
}

const CompanyIdIssuanceCard: React.FC<CompanyInfoRowProps> = ({
  data,
  editIndex,
  editFormData,
  onEdit,
  handleChange,
  handleUpdate,
  handleCancel,
  handleDelete,
  handleConfirmModalOpen,
  handleTextChange,
  handleOpen,
}) => {
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          fontWeight="fontWeightBold"
          sx={{
            display: "inline-block",
            borderBottom: "5px solid #FF8C00",
            paddingBottom: "0.5px",
            color: "text.primary",
          }}
        >
          検索結果：{convertNumberToFullWidthDigits(data.length)}件
        </Typography>
        <Button
          variant="contained"
          onClick={handleOpen}
          sx={{
            backgroundColor: "#CC5500",
            width: { xs: "18vh", sm: "20vh" },
          }}
        >
          新規登録
        </Button>
      </Box>
      <Stack spacing={1}>
        {data.map((row, index) => (
          <Grid2 container key={row.companyCode || index}>
            <Card
              key={index}
              sx={{
                width: "100%",
                p: 2,
                backgroundColor: theme.palette.custom.row,
              }}
            >
              <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                {/* 企業コード */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    企業コード：{row.companyCode}
                  </Typography>
                </Box>
                {/* 企業名 */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    企業名：{row.companyName}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Typography variant="body1">ステータス：</Typography>
                  {editIndex === index && row.status !== Status.Inactive ? (
                    <Select
                      name="status"
                      value={editFormData.status}
                      onChange={handleChange}
                      size="small"
                    >
                      <MenuItem value={Status.Active}>利用中</MenuItem>
                      <MenuItem value={Status.Stop}>停止中</MenuItem>
                    </Select>
                  ) : (
                    <Select
                      name="status"
                      value={row.status}
                      onChange={handleChange}
                      size="small"
                      disabled
                    >
                      <MenuItem value={Status.Active}>利用中</MenuItem>
                      <MenuItem value={Status.Stop}>停止中</MenuItem>
                      <MenuItem value={Status.Inactive}>利用なし</MenuItem>
                    </Select>
                  )}
                </Box>
                {/* 配信可能人数 (メール)*/}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    mb: 1,
                    gap: 1,
                  }}
                >
                  {/* テキスト部分 */}
                  <Typography variant="body1">
                    配信可能人数 (メール)：
                  </Typography>
                  {editIndex === index ? (
                    <TextField
                      size="small"
                      type="number"
                      name="mailSendLimit"
                      value={editFormData.mailSendLimit}
                      onChange={handleTextChange}
                      inputProps={{ maxLength: 255, min: 0 }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: "55px" }}
                    />
                  ) : (
                    <TextField
                      size="small"
                      type="number"
                      name="mailSendLimit"
                      disabled
                      value={row.mailSendLimit}
                      inputProps={{ maxLength: 255, min: 0 }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: "55px" }}
                    />
                  )}
                </Box>
                {/* 配信可能人数 (SMS)  */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    mb: 1,
                    gap: 1,
                  }}
                >
                  {/* テキスト部分 */}
                  <Typography variant="body1">配信可能人数 (SMS)：</Typography>
                  {/* TextField部分 (編集モード or 表示モード) */}
                  {editIndex === index ? (
                    <TextField
                      size="small"
                      type="number"
                      name="smsSendLimit"
                      value={editFormData.smsSendLimit}
                      onChange={handleTextChange}
                      inputProps={{ min: 0 }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: "55px" }}
                    />
                  ) : (
                    <TextField
                      size="small"
                      type="number"
                      name="smsSendLimit"
                      disabled
                      value={row.smsSendLimit}
                      inputProps={{ min: 0 }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: "55px" }}
                    />
                  )}
                </Box>
                {/* アクションボタン (編集モード対応) */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    mt: 2,
                  }}
                >
                  {editIndex === index ? (
                    <>
                      <Tooltip title={"保存"} placement="top" arrow>
                        <IconButton onClick={handleUpdate}>
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={"キャンセル"} placement="top" arrow>
                        <IconButton onClick={handleCancel}>
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title={"編集"} placement="top" arrow>
                        <IconButton onClick={() => onEdit(index)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={"削除"} placement="top" arrow>
                        <IconButton
                          onClick={() =>
                            handleConfirmModalOpen(
                              "選択した企業情報を削除します。よろしいですか？",
                              () => handleDelete(index)
                            )
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid2>
        ))}
      </Stack>
    </>
  );
};

export default CompanyIdIssuanceCard;
